import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

export default function StudentActionModal({ 
    selectedStudentAction, 
    setSelectedStudentAction, 
    usersMap, 
    activeClass, 
    handleBoardControlChange, 
    handleAssignQuickHW 
}) {
    const [studentProfile, setStudentProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // 🔥 Fetch the complete Lichess-style lifetime profile of the student
    useEffect(() => {
        const fetchProfile = async () => {
            if (!selectedStudentAction) return;
            setIsLoading(true);
            try {
                const safeEmail = selectedStudentAction.toLowerCase();
                const docSnap = await getDoc(doc(db, "users", safeEmail));
                if (docSnap.exists()) {
                    setStudentProfile(docSnap.data());
                }
            } catch (e) {
                console.error("Error fetching student profile:", e);
            }
            setIsLoading(false);
        };
        fetchProfile();
    }, [selectedStudentAction]);

    if (!selectedStudentAction) return null;

    const safeEmail = selectedStudentAction.replace(/\./g, '_');
    const studentName = usersMap[selectedStudentAction] || selectedStudentAction;
    const isOnline = activeClass?.presence && (Date.now() - activeClass.presence[safeEmail] < 45000);
    
    // Live Class Stats
    const arcadeStats = activeClass?.studentArcadeStats?.[safeEmail];
    const currentFen = activeClass?.studentFens?.[safeEmail] || '8/8/8/8/8/8/8/8 w - - 0 1';
    const isLocked = activeClass?.boardControl?.[selectedStudentAction];

    const copyStudentPosition = () => {
        navigator.clipboard.writeText(currentFen);
        toast.success(`${studentName}'s board position copied!`);
    };

    return (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{background: 'var(--bg-primary)', borderRadius: '16px', width: '95%', maxWidth: '450px', border: '2px solid #3b82f6', boxShadow: '0 10px 40px rgba(0,0,0,0.6)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                
                {/* 🌟 HEADER SECTION */}
                <div style={{background: 'var(--bg-primary)', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid rgba(163, 177, 198, 0.4)' }}>
                    <div style={{display: 'flex', gap: '15px' }}>
                        <div style={{width: '55px', height: '55px', background: '#3b82f6', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', boxShadow: '0 4px 10px rgba(59, 130, 246, 0.4)' }}>
                            {studentProfile?.avatar || '👤'}
                        </div>
                        <div>
                            <div style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {studentName}
                                <span title={isOnline ? "Online" : "Offline"} style={{width: '12px', height: '12px', background: isOnline ? '#10b981' : '#64748b', borderRadius: '50%', display: 'inline-block', border: '2px solid var(--bg-primary)' }}></span>
                            </div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '13px', marginTop: '2px' }}>{selectedStudentAction}</div>
                            
                            {/* Quick Lifetime Badges */}
                            {!isLoading && (
                                <div style={{display: 'flex', gap: '10px', marginTop: '8px' }}>
                                    <span style={{background: 'rgba(163, 177, 198, 0.4)', color: '#10b981', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>Level {studentProfile?.level || 1}</span>
                                    <span style={{background: 'rgba(163, 177, 198, 0.4)', color: '#fde047', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>⭐ {studentProfile?.totalXp || 0} XP</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <button onClick={() => setSelectedStudentAction(null)} style={{background: 'rgba(163, 177, 198, 0.4)', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>✖</button>
                </div>
                
                {/* 📜 SCROLLABLE BODY */}
                <div style={{padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {/* 🔴 LIVE CLASS STATS (Current Performance) */}
                    <div>
                        <h4 style={{margin: '0 0 10px 0', color: '#2563eb', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>🔴 Live Class Activity</h4>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div style={{background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', textAlign: 'center' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '2px', fontWeight: 'bold' }}>TASKS SOLVED</div>
                                <div style={{color: '#fde047', fontSize: '20px', fontWeight: 'bold' }}>⭐ {arcadeStats?.stars || 0}</div>
                            </div>
                            <div style={{background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', textAlign: 'center' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '2px', fontWeight: 'bold' }}>MISTAKES</div>
                                <div style={{color: '#ef4444', fontSize: '20px', fontWeight: 'bold' }}>❌ {arcadeStats?.mistakes || 0}</div>
                            </div>
                        </div>
                        
                        {/* Position Extractor */}
                        <div style={{background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px dashed #3b82f6', marginTop: '10px' }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 'bold' }}>Current Board FEN:</span>
                                <button onClick={copyStudentPosition} style={{padding: '4px 8px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>Copy FEN</button>
                            </div>
                            <p style={{color: '#64748b', fontSize: '11px', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>{currentFen}</p>
                        </div>
                    </div>

                    {/* 🏆 LIFETIME LICHESS-STYLE STATS */}
                    <div>
                        <h4 style={{margin: '0 0 10px 0', color: '#f59e0b', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>🏆 Lifetime History</h4>
                        {isLoading ? (
                            <div style={{color: '#64748b', textAlign: 'center', padding: '10px' }}>Loading history...</div>
                        ) : (
                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{display: 'flex', justifyContent: 'space-between', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', }}>
                                    <span style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold' }}>🧩 Puzzles Solved</span>
                                    <span style={{color: 'var(--text-primary)', fontWeight: 'bold' }}>{studentProfile?.puzzlesSolved || 0}</span>
                                </div>
                                <div style={{display: 'flex', justifyContent: 'space-between', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', }}>
                                    <span style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold' }}>⚔️ Games Played</span>
                                    <span style={{color: 'var(--text-primary)', fontWeight: 'bold' }}>{studentProfile?.gamesPlayed || 0}</span>
                                </div>
                                <div style={{display: 'flex', justifyContent: 'space-between', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', }}>
                                    <span style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold' }}>🎒 Homeworks Completed</span>
                                    <span style={{color: 'var(--text-primary)', fontWeight: 'bold' }}>{studentProfile?.homeworksDone || 0}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ⚡ ACTION CONTROLS */}
                    <div>
                        <h4 style={{margin: '0 0 10px 0', color: 'var(--text-secondary)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>⚡ Coach Controls</h4>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button 
                                onClick={() => { handleBoardControlChange(selectedStudentAction, isLocked ? 'none' : 'both'); setSelectedStudentAction(null); }} 
                                style={{padding: '12px', background: isLocked ? '#ef4444' : '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '14px', transition: '0.2s' }}
                            >
                                {isLocked ? '🔒 Board is Locked (Click to Free)' : '🔓 Board is Free (Click to Lock)'}
                            </button>
                            
                            <button 
                                onClick={() => { handleAssignQuickHW(selectedStudentAction); setSelectedStudentAction(null); }} 
                                style={{padding: '12px', background: '#f59e0b', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '14px', transition: '0.2s' }}
                            >
                                🎒 Assign Current Board as HW
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
